// ENG-2825 (plan D3 / round 3 C3): the one place that reads an inbox row's capability flags.
//
// Every inbox row now states what the toolbar may do with it (`can_download`, `can_label`,
// `can_notify`, `can_assign`, `can_sftp`) and what kind of document it is
// (`reply_document_state`). Before this, eight surfaces each inferred "not a document" from
// the presence of a reply-history `uri` (`isAReplySelected`, `isReplyRow`, `record-row-reply`,
// ...), which is exactly the inference a complete return document breaks: its row carries the
// history link AND is an ordinary document. Nothing outside this file may read `uri` to decide
// what a row can do.
//
// Rows without the flags fall back to the old `uri` inference. That covers a serializer that
// does not emit them and the rolling-deploy window where a new bundle talks to an old API pod;
// in both cases a reply row keeps behaving exactly as it did before the flags existed.

export const CAPABILITIES = Object.freeze({
  DOWNLOAD: 'can_download',
  LABEL: 'can_label',
  NOTIFY: 'can_notify',
  ASSIGN: 'can_assign',
  SFTP: 'can_sftp',
  SECURE_LINK: 'can_create_secure_link',
  ARCHIVE: 'can_archive',
});

export const DOCUMENT_STATES = Object.freeze({
  READY: 'ready',
  PREPARING: 'preparing',
  NEEDS_ATTENTION: 'needs_attention',
  UNAVAILABLE: 'unavailable',
});

const isRow = (record) => Boolean(record) && typeof record === 'object';
const documentRowKinds = new Set(['receiver', 'received_fax', 'email_attachment', 'jotform_submission', 'uploaded_record', 'reply']);

// A row that carries visible reply message/history (the drawer). Grant-only reply rows omit
// this uri; its presence says nothing about the document actions the row permits.
export const hasMessageHistory = (record) => isRow(record) && Boolean(record.uri);

// A reply row whose complete return document is ready and openable: it behaves as a document
// everywhere (title opens the viewer, click selects, viewer navigation includes it).
export const isCompleteDocumentRow = (record) => isRow(record)
  && record.reply_document_state === DOCUMENT_STATES.READY
  && Boolean(record.file_token);

// A row whose click opens the message drawer instead of following document conventions: a
// legacy reply, or a new return whose document is not ready yet. Replaces `isReplyRow` and the
// bare `if (uri)` row-click test.
export const opensMessageDrawerOnClick = (record) => hasMessageHistory(record)
  && !isCompleteDocumentRow(record);

// Whether ONE row denies a capability. `false` is the only denial; `undefined` (no flags) falls
// back to the legacy inference described above -- except for a reply row, which fails closed.
//
// ENG-3438: a reply row served to a user who holds only an assignment grant on its complete
// document is redacted -- no message `uri`, because the grant is the working document only
// (#3870). On a payload that omits the flags (an old API pod during a rolling deploy) the `uri`
// inference would read that redaction as "not a reply" and enable every action; the legacy
// inference was only ever `Boolean(uri)`, which is `true` for every reply row that has flags
// absent today, so answering `true` for the whole kind changes nothing for existing payloads.
export const rowLacksCapability = (record, capability) => {
  if (!isRow(record)) return false;
  if (record.receiver_type && !documentRowKinds.has(record.receiver_type)) return true;
  const value = record[capability];
  if (record.receiver_type === 'reply') {
    // A legacy signed return (no document state) keeps the pre-ENG-3438 viewer Archive and
    // Secure Link, which were never reply-gated; its viewer payload carries neither flag.
    if (!record.reply_document_state && value === undefined
      && (capability === CAPABILITIES.ARCHIVE || capability === CAPABILITIES.SECURE_LINK)) return false;
    // Source-authorized workflow archive survives rollback and unready documents, and the
    // original signed return keeps its secure link whatever its document's state. The server
    // encodes both explicitly (rows never carry the link flag); no legacy inference.
    if (capability === CAPABILITIES.ARCHIVE || capability === CAPABILITIES.SECURE_LINK) return value !== true;
    return capability === CAPABILITIES.SFTP || !isCompleteDocumentRow(record) || value !== true;
  }
  if (value === undefined) {
    return Boolean(record.uri);
  }
  return value === false;
};

// Whether ANY selected row denies a capability -- the drop-in replacement for the eight
// `isAReplySelected(selectedData)` copies, one call per action so each action asks about
// the capability it actually needs. Ignores the 'all' sentinel the tables put in selections.
export const selectionLacksCapability = (selectedData, capability) => (Array.isArray(selectedData) ? selectedData : [])
  .some((record) => record !== 'all' && rowLacksCapability(record, capability));

// Actions without their own capability flag still reject reply-backed rows, including
// grant-only documents whose history uri is redacted. Keep the uri fallback for legacy
// payloads without receiver_type; history and drawer access still use hasMessageHistory.
export const selectionHasReplyRow = (selectedData) => (Array.isArray(selectedData) ? selectedData : [])
  .some((record) => isRow(record) && (record.receiver_type === 'reply' || hasMessageHistory(record)));

// The viewers' and secure-link dialog's gate. `canCreateSecureLink` is the reducer's evaluation
// of the viewed payload (legacy returns included). The server already refuses an unready working
// document there; the original signed return keeps its link whatever its document's state. An
// unknown kind still refuses on its own. Ordinary records are never gated here.
export const secureLinkDenied = ({ receiverType, canCreateSecureLink }) => {
  if (receiverType && !documentRowKinds.has(receiverType)) return true;
  if (receiverType !== 'reply') return false;
  return !canCreateSecureLink;
};

export const documentStateOf = (record) => (isRow(record) ? record.reply_document_state || null : null);

// Choose complete-result semantics from the requested rows before token resolution. A hidden
// token and a nonexistent token must never select different backend download behavior.
export const selectionNeedsCompleteDownload = (records) => (Array.isArray(records) ? records : [])
  .some((record) => isRow(record) && record.receiver_type === 'reply');
